/**
 * General helper functions for Schema AI.
 *
 * @package Schema_AI
 * @since   1.0.0
 */

/**
 * Format an ISO date string to a human-readable format.
 *
 * @param {string} dateString - ISO 8601 date string.
 * @return {string} Formatted date (e.g. "Jan 15, 2025").
 */
export function formatDate( dateString ) {
    if ( ! dateString ) {
        return '';
    }

    try {
        const date = new Date( dateString );
        if ( isNaN( date.getTime() ) ) {
            return dateString;
        }

        return date.toLocaleDateString( 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        } );
    } catch {
        return dateString;
    }
}

/**
 * Format a schema type string into a human-readable name.
 *
 * @param {string} type - The schema.org type (e.g. "LocalBusiness").
 * @return {string} Human-readable name (e.g. "Local Business").
 */
export function formatSchemaType( type ) {
    if ( ! type ) {
        return 'Unknown';
    }

    const typeMap = {
        Article: 'Article',
        BlogPosting: 'Blog Post',
        NewsArticle: 'News Article',
        Product: 'Product',
        LocalBusiness: 'Local Business',
        Organization: 'Organization',
        Person: 'Person',
        Event: 'Event',
        Recipe: 'Recipe',
        FAQPage: 'FAQ',
        HowTo: 'How-To',
        Review: 'Review',
        Service: 'Service',
        BreadcrumbList: 'Breadcrumb',
        VideoObject: 'Video',
        ImageObject: 'Image',
        Course: 'Course',
        WebPage: 'Web Page',
        WebSite: 'Website',
        SoftwareApplication: 'Software App',
        Restaurant: 'Restaurant',
        MedicalCondition: 'Medical Condition',
        JobPosting: 'Job Posting',
    };

    return typeMap[ type ] || type.replace( /([A-Z])/g, ' $1' ).trim();
}

/**
 * Get a dashicon name corresponding to a schema type.
 *
 * @param {string} type - The schema.org type.
 * @return {string} Dashicon name (without 'dashicons-' prefix).
 */
export function getSchemaTypeIcon( type ) {
    const iconMap = {
        Article: 'media-document',
        BlogPosting: 'admin-post',
        NewsArticle: 'media-document',
        Product: 'cart',
        LocalBusiness: 'store',
        Organization: 'building',
        Person: 'admin-users',
        Event: 'calendar-alt',
        Recipe: 'food',
        FAQPage: 'editor-help',
        HowTo: 'editor-ol',
        Review: 'star-filled',
        Service: 'admin-tools',
        BreadcrumbList: 'arrow-right-alt',
        VideoObject: 'video-alt3',
        ImageObject: 'format-image',
        Course: 'welcome-learn-more',
        WebPage: 'admin-site-alt3',
        WebSite: 'admin-site',
        SoftwareApplication: 'laptop',
        Restaurant: 'food',
        JobPosting: 'businessman',
    };

    return iconMap[ type ] || 'editor-code';
}

/**
 * Truncate text to a specified length with ellipsis.
 *
 * @param {string} text   - The text to truncate.
 * @param {number} length - Maximum length before truncation.
 * @return {string} Truncated text with ellipsis if needed.
 */
export function truncateText( text, length = 100 ) {
    if ( ! text || text.length <= length ) {
        return text || '';
    }

    return text.substring( 0, length ).trimEnd() + '...';
}

/**
 * Create a debounced version of a function.
 *
 * @param {Function} fn    - The function to debounce.
 * @param {number}   delay - Delay in milliseconds.
 * @return {Function} The debounced function.
 */
export function debounce( fn, delay = 300 ) {
    let timer = null;

    return function ( ...args ) {
        if ( timer ) {
            clearTimeout( timer );
        }

        timer = setTimeout( () => {
            fn.apply( this, args );
            timer = null;
        }, delay );
    };
}

/**
 * Copy text to the clipboard.
 *
 * @param {string} text - The text to copy.
 * @return {Promise<boolean>} True if copied successfully.
 */
export async function copyToClipboard( text ) {
    try {
        if ( navigator.clipboard && navigator.clipboard.writeText ) {
            await navigator.clipboard.writeText( text );
            return true;
        }

        // Fallback for older browsers.
        const textarea = document.createElement( 'textarea' );
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild( textarea );
        textarea.select();
        const success = document.execCommand( 'copy' );
        document.body.removeChild( textarea );
        return success;
    } catch {
        return false;
    }
}

/**
 * Get a CSS class for a validation status.
 *
 * @param {string} status - The validation status string.
 * @return {string} CSS class suffix.
 */
export function getValidationStatusClass( status ) {
    const map = {
        valid: 'success',
        warning: 'warning',
        invalid: 'error',
        pending: 'pending',
        error: 'error',
    };

    return map[ status ] || 'pending';
}

/**
 * Format a confidence score as a percentage string.
 *
 * @param {number|null} score - Confidence score between 0 and 1.
 * @return {string} Formatted percentage (e.g. "85%").
 */
export function formatConfidence( score ) {
    if ( score === null || score === undefined ) {
        return 'N/A';
    }

    return `${ Math.round( score * 100 ) }%`;
}

/**
 * Get a color class based on confidence score.
 *
 * @param {number} score - Confidence score between 0 and 1.
 * @return {string} Tailwind color class name.
 */
export function getConfidenceColor( score ) {
    if ( score >= 0.8 ) {
        return 'text-green-600';
    }
    if ( score >= 0.6 ) {
        return 'text-yellow-600';
    }
    return 'text-red-600';
}
